/**
 * @fileoverview Group Context Type Definitions
 * 小組上下文的嚴格類型定義
 */

/**
 * @typedef {'chat' | 'sticker' | 'auto'} GroupContextMode
 * 小組模式類型
 */

/**
 * @typedef {'real-time-chat' | 'idea-creation' | 'unknown'} InteractionType
 * 互動類型
 */

/**
 * @typedef {'學生' | '夥伴' | '教師'} MemberRole
 * 成員角色
 */

/**
 * @typedef {Object} GroupMember
 * 小組成員資訊
 * @property {string} name - 成員名稱
 * @property {MemberRole} role - 成員角色
 */

/**
 * @typedef {Object} GroupContext
 * 小組上下文完整類型定義
 * 
 * @property {string} activityId - 活動 ID
 * @property {string} activityTitle - 活動標題
 * @property {string} activityDescription - 活動描述
 * 
 * @property {string} groupId - 小組 ID
 * @property {string} groupName - 小組名稱
 * @property {number} memberCount - 成員數量
 * @property {GroupMember[]} members - 成員列表
 * 
 * @property {boolean} isPartnerChat - 是否為夥伴對話
 * @property {GroupContextMode} mode - 模式
 * @property {InteractionType} interactionType - 互動類型
 * @property {string} expectation - 預期行為描述
 * 
 * @property {ConversationHistoryItem[]} conversationHistory - 對話歷史（整合在 context 中）
 * 
 * @property {string} timestamp - ISO 格式時間戳
 * @property {string} contextVersion - Context 版本號
 */

/**
 * @typedef {'recent' | 'relevance' | 'hybrid'} HistoryStrategy
 * 對話歷史選擇策略
 */

/**
 * @typedef {Object} HistoryFormatOptions
 * 對話歷史格式化選項
 * @property {HistoryStrategy} [strategy='hybrid'] - 選擇策略
 * @property {string[]} [keywords=[]] - 關鍵字列表
 */

/**
 * @typedef {Object} ConversationHistoryItem
 * 對話歷史項目
 * @property {string} id - 節點 ID
 * @property {string} title - 標題
 * @property {string} content - 內容
 * @property {string} author - 作者
 * @property {string} [role] - 角色（'user' 或 'assistant'）
 * @property {string} timestamp - 時間戳
 * @property {string} nodeType - 節點類型
 * @property {number} relevanceScore - 相關性分數
 */

/**
 * @typedef {Object} UserMessage
 * 用戶消息格式
 * @property {string} id - 消息 ID
 * @property {string} content - 消息內容
 * @property {string} author - 作者
 * @property {Date | string} timestamp - 時間戳
 */

/**
 * @typedef {Object} ContextValidationResult
 * Context 驗證結果
 * @property {boolean} isValid - 是否有效
 * @property {string[]} issues - 問題列表
 * @property {number} score - 完整性評分 (0-1)
 */

/**
 * ConversationHistory 管理類
 * 封裝對話歷史列表的操作，提供類型安全和便捷方法
 */
export class ConversationHistory {
  /**
   * @private
   * @type {ConversationHistoryItem[]}
   */
  _items = [];

  /**
   * 構造函數
   * @param {ConversationHistoryItem[]} [items=[]] - 初始項目列表
   */
  constructor(items = []) {
    this._items = this._validateAndFilter(items);
  }

  /**
   * 驗證並過濾項目列表
   * @private
   * @param {any[]} items - 原始項目
   * @returns {ConversationHistoryItem[]} 有效的項目列表
   */
  _validateAndFilter(items) {
    if (!Array.isArray(items)) return [];
    
    return items.filter(item => {
      const isValid = GroupContextValidator.isConversationHistoryItem(item);
      if (!isValid) {
        console.warn('[ConversationHistory] 過濾無效項目:', item);
      }
      return isValid;
    });
  }

  /**
   * 獲取所有項目
   * @returns {ConversationHistoryItem[]}
   */
  getItems() {
    return [...this._items];
  }

  /**
   * 獲取項目數量
   * @returns {number}
   */
  getCount() {
    return this._items.length;
  }

  /**
   * 添加單個或多個項目
   * @param {ConversationHistoryItem | ConversationHistoryItem[]} items - 要添加的項目
   * @returns {ConversationHistory} 返回 this 支持鏈式調用
   */
  add(items) {
    const itemsArray = Array.isArray(items) ? items : [items];
    const validItems = this._validateAndFilter(itemsArray);
    this._items.push(...validItems);
    return this;
  }

  /**
   * 限制項目數量（保留最新的 N 項）
   * @param {number} limit - 最大數量
   * @returns {ConversationHistory} 返回 this 支持鏈式調用
   */
  limit(limit) {
    if (typeof limit === 'number' && limit > 0) {
      this._items = this._items.slice(0, limit);
    }
    return this;
  }

  /**
   * 根據條件過濾項目
   * @param {(item: ConversationHistoryItem) => boolean} predicate - 過濾條件
   * @returns {ConversationHistory} 返回新的 ConversationHistory 實例
   */
  filter(predicate) {
    return new ConversationHistory(this._items.filter(predicate));
  }

  /**
   * 根據作者過濾
   * @param {string} author - 作者名稱
   * @returns {ConversationHistory} 返回新的 ConversationHistory 實例
   */
  filterByAuthor(author) {
    return this.filter(item => item.author === author);
  }

  /**
   * 根據角色過濾
   * @param {'user' | 'assistant'} role - 角色
   * @returns {ConversationHistory} 返回新的 ConversationHistory 實例
   */
  filterByRole(role) {
    return this.filter(item => item.role === role);
  }

  /**
   * 去重（基於 ID）
   * @returns {ConversationHistory} 返回新的 ConversationHistory 實例
   */
  deduplicate() {
    const seenIds = new Set();
    const uniqueItems = this._items.filter(item => {
      if (seenIds.has(item.id)) return false;
      seenIds.add(item.id);
      return true;
    });
    return new ConversationHistory(uniqueItems);
  }

  /**
   * 排序（按時間戳）
   * @param {'asc' | 'desc'} order - 排序方向
   * @returns {ConversationHistory} 返回新的 ConversationHistory 實例
   */
  sortByTimestamp(order = 'desc') {
    const sorted = [...this._items].sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return order === 'asc' ? timeA - timeB : timeB - timeA;
    });
    return new ConversationHistory(sorted);
  }

  /**
   * 轉換為純數組（用於序列化）
   * @returns {ConversationHistoryItem[]}
   */
  toArray() {
    return this.getItems();
  }

  /**
   * 從數組創建 ConversationHistory 實例
   * @param {ConversationHistoryItem[]} items - 項目列表
   * @returns {ConversationHistory}
   */
  static fromArray(items) {
    return new ConversationHistory(items);
  }

  /**
   * 創建空的 ConversationHistory
   * @returns {ConversationHistory}
   */
  static empty() {
    return new ConversationHistory([]);
  }
}

/**
 * GroupContext 類型驗證和管理類（物件導向設計）
 * 提供統一的驗證、構建和更新邏輯，避免重複實現
 */
export class GroupContextValidator {
  /**
   * 驗證字段的類型和必填性
   * @private
   */
  static _validateField(value, type, required = true) {
    if (required && (value === undefined || value === null)) {
      return { valid: false, issue: 'required field missing' };
    }
    if (!required && (value === undefined || value === null)) {
      return { valid: true };
    }
    if (typeof value !== type) {
      return { valid: false, issue: `expected ${type}, got ${typeof value}` };
    }
    return { valid: true };
  }

  /**
   * 驗證是否為有效的 GroupMember
   * @param {any} obj - 要驗證的對象
   * @returns {obj is GroupMember}
   */
  static isGroupMember(obj) {
    if (!obj) return false;
    const nameCheck = this._validateField(obj.name, 'string');
    if (!nameCheck.valid) return false;
    return ['學生', '夥伴', '教師'].includes(obj.role);
  }

  /**
   * 驗證是否為有效的 ConversationHistoryItem
   * @param {any} obj - 要驗證的對象
   * @returns {obj is ConversationHistoryItem}
   */
  static isConversationHistoryItem(obj) {
    if (!obj) return false;
    const idCheck = this._validateField(obj.id, 'string');
    const authorCheck = this._validateField(obj.author, 'string');
    const timestampCheck = this._validateField(obj.timestamp, 'string');
    return idCheck.valid && authorCheck.valid && timestampCheck.valid;
  }

  /**
   * 驗證是否為有效的 GroupContext
   * @param {any} obj - 要驗證的對象
   * @returns {obj is GroupContext}
   */
  static isGroupContext(obj) {
    if (!obj || typeof obj !== 'object') return false;
    
    // 必填字段驗證
    const requiredFields = [
      ['activityId', 'string'],
      ['activityTitle', 'string'],
      ['activityDescription', 'string'],
      ['groupId', 'string'],
      ['groupName', 'string'],
      ['memberCount', 'number'],
      ['members', 'object'],
      ['isPartnerChat', 'boolean'],
      ['mode', 'string'],
      ['interactionType', 'string'],
      ['expectation', 'string'],
      ['conversationHistory', 'object'],
      ['timestamp', 'string'],
      ['contextVersion', 'string']
    ];
    
    for (const [field, type] of requiredFields) {
      const check = this._validateField(obj[field], type);
      if (!check.valid) return false;
    }
    
    // 驗證 members 是否為數組且包含有效成員
    if (!Array.isArray(obj.members)) return false;
    if (!obj.members.every(m => this.isGroupMember(m))) return false;
    
    // 驗證 conversationHistory 是否為數組
    if (!Array.isArray(obj.conversationHistory)) return false;
    
    return true;
  }

  /**
   * 詳細驗證 GroupContext（返回詳細信息）
   * @param {any} obj - 要驗證的對象
   * @returns {ContextValidationResult}
   */
  static validateDetailed(obj) {
    const issues = [];
    let validFields = 0;
    const totalFields = 14;
    
    if (!obj || typeof obj !== 'object') {
      return { isValid: false, issues: ['Not an object'], score: 0 };
    }
    
    // 必填字段驗證
    const requiredFields = [
      ['activityId', 'string'],
      ['activityTitle', 'string'],
      ['activityDescription', 'string'],
      ['groupId', 'string'],
      ['groupName', 'string'],
      ['memberCount', 'number'],
      ['members', 'object'],
      ['isPartnerChat', 'boolean'],
      ['mode', 'string'],
      ['interactionType', 'string'],
      ['expectation', 'string'],
      ['conversationHistory', 'object'],
      ['timestamp', 'string'],
      ['contextVersion', 'string']
    ];
    
    for (const [field, type] of requiredFields) {
      const check = this._validateField(obj[field], type);
      if (check.valid) {
        validFields++;
      } else {
        issues.push(`${field}: ${check.issue}`);
      }
    }
    
    // 驗證 members 數組
    if (Array.isArray(obj.members)) {
      const invalidMembers = obj.members.filter(m => !this.isGroupMember(m));
      if (invalidMembers.length > 0) {
        issues.push(`Invalid members: ${invalidMembers.length}`);
      }
    }
    
    // 驗證 conversationHistory 數組
    if (Array.isArray(obj.conversationHistory)) {
      const invalidItems = obj.conversationHistory.filter(item => !this.isConversationHistoryItem(item));
      if (invalidItems.length > 0) {
        issues.push(`Invalid conversation history items: ${invalidItems.length}`);
      }
    }
    
    const score = validFields / totalFields;
    const isValid = issues.length === 0 && score === 1;
    
    return { isValid, issues, score };
  }

  /**
   * 創建基礎 GroupContext
   * @param {Object} params - 參數
   * @returns {GroupContext}
   */
  static createBase(params) {
    return {
      activityId: params.activityId || '',
      activityTitle: params.activityTitle || '',
      activityDescription: params.activityDescription || '',
      
      groupId: params.groupId || '',
      groupName: params.groupName || '',
      memberCount: params.memberCount || 0,
      members: params.members || [],
      
      isPartnerChat: params.isPartnerChat || false,
      mode: params.mode || 'auto',
      interactionType: params.interactionType || 'unknown',
      expectation: params.expectation || '',
      
      conversationHistory: [],
      
      timestamp: new Date().toISOString(),
      contextVersion: params.contextVersion || '3.0-base'
    };
  }

  /**
   * 更新 GroupContext
   * @param {GroupContext} context - 原始 context
   * @param {Partial<GroupContext>} updates - 更新內容
   * @returns {GroupContext}
   */
  static update(context, updates) {
    return {
      ...context,
      ...updates,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * 添加對話歷史到 GroupContext
   * @param {GroupContext} context - 原始 context
   * @param {ConversationHistoryItem[]} items - 要添加的歷史項目
   * @returns {GroupContext}
   */
  static addConversationHistory(context, items) {
    const validItems = items.filter(item => this.isConversationHistoryItem(item));
    return this.update(context, {
      conversationHistory: [...context.conversationHistory, ...validItems]
    });
  }

  /**
   * 從 ConversationHistory 實例創建 GroupContext（便捷方法）
   * @param {Object} params - 基礎參數
   * @param {ConversationHistory} conversationHistory - ConversationHistory 實例
   * @returns {GroupContext}
   */
  static createWithHistory(params, conversationHistory) {
    const base = this.createBase(params);
    return {
      ...base,
      conversationHistory: conversationHistory instanceof ConversationHistory 
        ? conversationHistory.toArray() 
        : conversationHistory
    };
  }
}

// 向後兼容：導出靜態方法作為對象
export const GroupContextTypes = {
  isGroupContext: (obj) => GroupContextValidator.isGroupContext(obj),
  isGroupMember: (obj) => GroupContextValidator.isGroupMember(obj),
  isConversationHistoryItem: (obj) => GroupContextValidator.isConversationHistoryItem(obj),
  validateDetailed: (obj) => GroupContextValidator.validateDetailed(obj),
  createBase: (params) => GroupContextValidator.createBase(params),
  update: (context, updates) => GroupContextValidator.update(context, updates),
  addConversationHistory: (context, items) => GroupContextValidator.addConversationHistory(context, items),
  createWithHistory: (params, history) => GroupContextValidator.createWithHistory(params, history)
};

export default GroupContextValidator;

